import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Target, TrendingUp, CheckCircle2, AlertCircle, BookOpen, Award, ArrowRight, RotateCcw, BarChart2, ChevronRight, LayoutDashboard } from 'lucide-react';
import { AssessmentReport } from './types';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { ConfettiCelebration } from '../ConfettiCelebration';
import { Badge } from '../ui/Badge';
interface AssessmentReportPageProps {
  report: AssessmentReport;
  onExit: () => void;
  onRetake?: () => void;
  onPracticeWeakAreas?: () => void;
}
type Tab = 'overzicht' | 'vakken' | 'aandacht' | 'actie';
export function AssessmentReportPage({
  report,
  onExit,
  onRetake,
  onPracticeWeakAreas
}: AssessmentReportPageProps) {
  const [activeTab, setActiveTab] = useState<Tab>('overzicht');
  const {
    score,
    estimatedLevels,
    overallEstimatedLevelCode,
    overallExpectedLevelCode,
    growthIndicators,
    focusAreasBySkill,
    strengthsBySkill,
    actionPlan
  } = report;
  // Determine celebration level based on score
  const showConfetti = score.percentage >= 70;
  const confettiMessage = score.percentage >= 90 ? 'WAUW! Je bent een echte meester! 🎓' : score.percentage >= 80 ? 'Super gedaan! 🌟' : score.percentage >= 70 ? 'Heel goed bezig! 💪' : '';
  const tabs = [{
    id: 'overzicht' as Tab,
    label: 'Overzicht',
    icon: LayoutDashboard
  }, {
    id: 'vakken' as Tab,
    label: 'Per Vak',
    icon: BarChart2
  }, {
    id: 'aandacht' as Tab,
    label: 'Sterk & Zwak',
    icon: Target
  }, {
    id: 'actie' as Tab,
    label: 'Actieplan',
    icon: TrendingUp
  }];
  // Helper for Rocket icon since it's not imported above
  function Rocket(props: any) {
    return <TrendingUp {...props} data-id="element-1128" />;
  }
  return <div className="h-screen flex flex-col bg-slate-50 overflow-hidden font-sans" data-id="element-1129">
      <ConfettiCelebration trigger={showConfetti} message={confettiMessage} data-id="element-1130" />

      {/* Header */}
      <div className="flex-none bg-white border-b border-slate-200 px-4 py-4 shadow-sm z-10" data-id="element-1131">
        <div className="max-w-5xl mx-auto flex items-center justify-between" data-id="element-1132">
          <div className="flex items-center gap-4" data-id="element-1133">
            <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center" data-id="element-1134">
              <Trophy className="w-6 h-6 text-indigo-600" data-id="element-1135" />
            </div>
            <div data-id="element-1136">
              <h1 className="text-xl font-black text-slate-900 tracking-tight" data-id="element-1137">
                Mijn Leerlingprofiel
              </h1>
              <p className="text-sm text-slate-500 font-medium" data-id="element-1138">
                Groep {report.selectedGroup} ·{' '}
                {new Date(report.completedAt).toLocaleDateString('nl-NL', {
                month: 'long',
                year: 'numeric'
              })}
              </p>
            </div>
          </div>
          <Button onClick={onExit} variant="ghost" size="sm" className="text-slate-600 hover:bg-slate-100" data-id="element-1139">
            Sluiten
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex-none bg-white border-b border-slate-200 px-4" data-id="element-1140">
        <div className="max-w-5xl mx-auto" data-id="element-1141">
          <div className="flex gap-2 overflow-x-auto no-scrollbar" data-id="element-1142">
            {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-4 font-bold text-sm transition-all relative whitespace-nowrap ${isActive ? 'text-indigo-600' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-1143">
                  <Icon className={`w-4 h-4 ${isActive ? 'stroke-[2.5px]' : ''}`} data-id="element-1144" />
                  <span data-id="element-1145">{tab.label}</span>
                  {isActive && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-t-full" data-id="element-1146" />}
                </button>;
          })}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-y-auto bg-slate-50/50" data-id="element-1147">
        <div className="max-w-5xl mx-auto p-4 sm:p-6 md:p-8" data-id="element-1148">
          <AnimatePresence mode="wait" data-id="element-1149">
            {/* OVERZICHT TAB */}
            {activeTab === 'overzicht' && <motion.div key="overzicht" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="space-y-6" data-id="element-1150">
                {/* Hero Card */}
                <Card className="p-8 bg-gradient-to-br from-indigo-600 to-violet-700 text-white border-0 shadow-xl overflow-hidden relative" data-id="element-1151">
                  {/* Background decoration */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-20 -mt-20 blur-3xl" data-id="element-1152"></div>
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-indigo-900/30 rounded-full -ml-10 -mb-10 blur-2xl" data-id="element-1153"></div>

                  <div className="relative z-10 flex flex-col md:flex-row items-center gap-8 md:gap-12" data-id="element-1154">
                    <div className="text-center md:text-left flex-1" data-id="element-1155">
                      <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-bold mb-4 border border-white/20" data-id="element-1156">
                        <Star className="w-4 h-4 text-yellow-300 fill-yellow-300" data-id="element-1157" />
                        <span data-id="element-1158">Jouw resultaat</span>
                      </div>
                      <h2 className="text-3xl md:text-4xl font-black mb-2 tracking-tight" data-id="element-1159">
                        Niveau {overallEstimatedLevelCode}
                      </h2>
                      <p className="text-indigo-100 text-lg font-medium mb-6" data-id="element-1160">
                        {overallEstimatedLevelCode === overallExpectedLevelCode ? 'Je zit precies op het verwachte niveau. Goed bezig!' : overallEstimatedLevelCode > overallExpectedLevelCode ? 'Wauw! Je zit boven het verwachte niveau!' : 'Je bent goed op weg. Blijf oefenen!'}
                      </p>

                      <div className="flex flex-wrap gap-4 justify-center md:justify-start" data-id="element-1161">
                        <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm border border-white/10" data-id="element-1162">
                          <div className="text-xs text-indigo-200 uppercase font-bold tracking-wider mb-1" data-id="element-1163">
                            Score
                          </div>
                          <div className="text-2xl font-black" data-id="element-1164">
                            {score.percentage}%
                          </div>
                        </div>
                        <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm border border-white/10" data-id="element-1165">
                          <div className="text-xs text-indigo-200 uppercase font-bold tracking-wider mb-1" data-id="element-1166">
                            Verwacht
                          </div>
                          <div className="text-2xl font-black" data-id="element-1167">
                            {overallExpectedLevelCode}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Visual Level Indicator */}
                    <div className="w-full md:w-auto flex justify-center" data-id="element-1168">
                      <div className="relative w-48 h-48" data-id="element-1169">
                        <svg className="w-full h-full transform -rotate-90" data-id="element-1170">
                          <circle cx="50%" cy="50%" r="45%" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="12" data-id="element-1171" />
                          <motion.circle cx="50%" cy="50%" r="45%" fill="none" stroke="white" strokeWidth="12" strokeLinecap="round" initial={{
                        pathLength: 0
                      }} animate={{
                        pathLength: score.percentage / 100
                      }} transition={{
                        duration: 1.5,
                        ease: 'easeOut'
                      }} style={{
                        strokeDasharray: '283',
                        strokeDashoffset: '0'
                      }} data-id="element-1172" />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center text-center" data-id="element-1173">
                          <div className="text-5xl font-black" data-id="element-1174">
                            {score.correctCount}
                          </div>
                          <div className="text-sm font-medium text-indigo-200" data-id="element-1175">
                            van {score.totalQuestions} goed
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Growth Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1176">
                  <Card className="p-6 border-l-4 border-l-emerald-500" data-id="element-1177">
                    <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-1178">
                      <TrendingUp className="w-5 h-5 text-emerald-500" data-id="element-1179" />
                      Waar je groeit
                    </h3>
                    <div className="space-y-3" data-id="element-1180">
                      {growthIndicators.filter(g => ['groei_snel', 'gaat_goed', 'ik_groei'].includes(g.level)).slice(0, 3).map(g => {
                    const module = estimatedLevels.find(m => m.moduleId === g.moduleId);
                    return <div key={g.moduleId} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-id="element-1181">
                              <div className="font-medium text-slate-700" data-id="element-1182">
                                {module?.moduleTitle}
                              </div>
                              <div className={`text-sm font-bold ${g.color} flex items-center gap-1`} data-id="element-1183">
                                {g.emoji} {g.label}
                              </div>
                            </div>;
                  })}
                      {growthIndicators.filter(g => ['groei_snel', 'gaat_goed', 'ik_groei'].includes(g.level)).length === 0 && <p className="text-slate-500 italic text-sm" data-id="element-1184">
                          Nog geen sterke groei zichtbaar. Blijf oefenen!
                        </p>}
                    </div>
                  </Card>

                  <Card className="p-6 border-l-4 border-l-amber-500" data-id="element-1185">
                    <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-1186">
                      <Target className="w-5 h-5 text-amber-500" data-id="element-1187" />
                      Aandachtspunten
                    </h3>
                    <div className="space-y-3" data-id="element-1188">
                      {growthIndicators.filter(g => ['let_op', 'extra_aandacht'].includes(g.level)).slice(0, 3).map(g => {
                    const module = estimatedLevels.find(m => m.moduleId === g.moduleId);
                    return <div key={g.moduleId} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-id="element-1189">
                              <div className="font-medium text-slate-700" data-id="element-1190">
                                {module?.moduleTitle}
                              </div>
                              <div className={`text-sm font-bold ${g.color} flex items-center gap-1`} data-id="element-1191">
                                {g.emoji} {g.label}
                              </div>
                            </div>;
                  })}
                      {growthIndicators.filter(g => ['let_op', 'extra_aandacht'].includes(g.level)).length === 0 && <p className="text-slate-500 italic text-sm" data-id="element-1192">
                          Geen specifieke aandachtspunten. Super!
                        </p>}
                    </div>
                  </Card>
                </div>
              </motion.div>}

            {/* VAKKEN TAB */}
            {activeTab === 'vakken' && <motion.div key="vakken" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="space-y-4" data-id="element-1193">
                <Card className="overflow-hidden" data-id="element-1194">
                  <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center" data-id="element-1195">
                    <h3 className="font-bold text-slate-700" data-id="element-1196">Niveau per vak</h3>
                    <div className="text-sm text-slate-500 flex items-center gap-2" data-id="element-1197">
                      <span className="w-2 h-2 rounded-full bg-slate-300" data-id="element-1198"></span>
                      Verwacht niveau:{' '}
                      <strong data-id="element-1199">{overallExpectedLevelCode}</strong>
                    </div>
                  </div>
                  <div className="divide-y divide-slate-100" data-id="element-1200">
                    {estimatedLevels.map(level => {
                  const growth = growthIndicators.find(g => g.moduleId === level.moduleId);
                  const isUnknown = level.questionCount < 3;
                  return <div key={level.moduleId} className="p-6 hover:bg-slate-50 transition-colors" data-id="element-1201">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-3" data-id="element-1202">
                            <div className="w-32 font-bold text-slate-900" data-id="element-1203">
                              {level.moduleTitle}
                            </div>
                            <div className="flex-1" data-id="element-1204">
                              {/* Niveau Bar */}
                              <div className="h-4 bg-slate-100 rounded-full overflow-hidden relative" data-id="element-1205">
                                {/* Expected Marker */}
                                <div className="absolute top-0 bottom-0 w-0.5 bg-slate-400 z-10 border-r border-dashed border-slate-600" style={{
                            left: `${level.expectedTier / 16 * 100}%`
                          }} data-id="element-1206" />

                                {/* Actual Level */}
                                <motion.div initial={{
                            width: 0
                          }} animate={{
                            width: `${level.estimatedTier / 16 * 100}%`
                          }} transition={{
                            duration: 1,
                            ease: 'easeOut'
                          }} className={`h-full rounded-full ${isUnknown ? 'bg-slate-300' : level.delta >= 0 ? 'bg-emerald-500' : level.delta >= -1 ? 'bg-yellow-400' : 'bg-amber-500'}`} data-id="element-1207" />
                              </div>
                            </div>
                            <div className="w-24 text-right font-bold text-slate-700" data-id="element-1208">
                              {isUnknown ? '?' : level.estimatedLevelCode}
                            </div>
                          </div>

                          <div className="flex justify-between items-center text-sm pl-0 sm:pl-36" data-id="element-1209">
                            <div className="flex items-center gap-2" data-id="element-1210">
                              {growth && <span className={`font-medium ${growth.color} flex items-center gap-1`} data-id="element-1211">
                                  {growth.emoji} {growth.label}
                                </span>}
                            </div>
                            <div className="text-slate-400 text-xs" data-id="element-1212">
                              {level.questionCount} vragen
                            </div>
                          </div>
                        </div>;
                })}
                  </div>
                </Card>
              </motion.div>}

            {/* AANDACHT TAB (Sterk & Zwak) */}
            {activeTab === 'aandacht' && <motion.div key="aandacht" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1213">
                {/* Sterke Punten */}
                <Card className="border-t-4 border-t-emerald-500 h-full" data-id="element-1214">
                  <div className="p-6 border-b border-slate-100 bg-emerald-50/30" data-id="element-1215">
                    <div className="flex items-center gap-3" data-id="element-1216">
                      <div className="bg-emerald-100 p-2 rounded-lg" data-id="element-1217">
                        <Star className="w-5 h-5 text-emerald-600 fill-emerald-600" data-id="element-1218" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-1219">
                        Sterke Punten
                      </h3>
                    </div>
                  </div>
                  <div className="p-6 space-y-6" data-id="element-1220">
                    {strengthsBySkill.length > 0 ? strengthsBySkill.slice(0, 3).map(area => <div key={area.moduleId} data-id="element-1221">
                          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-1222">
                            {area.moduleTitle}
                          </div>
                          <ul className="space-y-3" data-id="element-1223">
                            {area.skills.map(skill => <li key={skill.skillName} className="flex items-start gap-3" data-id="element-1224">
                                <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-1225" />
                                <div data-id="element-1226">
                                  <div className="font-semibold text-slate-800 capitalize" data-id="element-1227">
                                    {skill.skillName}
                                  </div>
                                  <div className="text-sm text-slate-500" data-id="element-1228">
                                    {skill.families.length} onderwerpen ·{' '}
                                    {Math.round(skill.avgAccuracy)}% goed
                                  </div>
                                </div>
                              </li>)}
                          </ul>
                        </div>) : <div className="text-center py-8 text-slate-400 italic" data-id="element-1229">
                        Nog geen specifieke sterke punten gevonden.
                      </div>}
                  </div>
                </Card>

                {/* Aandachtspunten */}
                <Card className="border-t-4 border-t-amber-500 h-full" data-id="element-1230">
                  <div className="p-6 border-b border-slate-100 bg-amber-50/30" data-id="element-1231">
                    <div className="flex items-center gap-3" data-id="element-1232">
                      <div className="bg-amber-100 p-2 rounded-lg" data-id="element-1233">
                        <Target className="w-5 h-5 text-amber-600" data-id="element-1234" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-1235">
                        Aandachtspunten
                      </h3>
                    </div>
                  </div>
                  <div className="p-6 space-y-6" data-id="element-1236">
                    {focusAreasBySkill.length > 0 ? focusAreasBySkill.slice(0, 3).map(area => <div key={area.moduleId} data-id="element-1237">
                          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2" data-id="element-1238">
                            {area.moduleTitle}
                          </div>
                          <ul className="space-y-3" data-id="element-1239">
                            {area.skills.map(skill => <li key={skill.skillName} className="flex items-start gap-3" data-id="element-1240">
                                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-1241" />
                                <div data-id="element-1242">
                                  <div className="font-semibold text-slate-800 capitalize" data-id="element-1243">
                                    {skill.skillName}
                                  </div>
                                  <div className="text-sm text-slate-500" data-id="element-1244">
                                    {skill.families.length} onderwerpen ·{' '}
                                    {Math.round(skill.avgAccuracy)}% goed
                                  </div>
                                </div>
                              </li>)}
                          </ul>
                        </div>) : <div className="text-center py-8 text-slate-400 italic" data-id="element-1245">
                        Geen specifieke aandachtspunten gevonden.
                      </div>}
                  </div>
                </Card>
              </motion.div>}

            {/* ACTIEPLAN TAB */}
            {activeTab === 'actie' && <motion.div key="actie" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} className="space-y-6" data-id="element-1246">
                {/* 1. Begin Met */}
                {actionPlan.beginMet && <Card className="border-l-4 border-l-indigo-600 overflow-hidden" data-id="element-1247">
                    <div className="p-6" data-id="element-1248">
                      <div className="flex items-center gap-2 mb-4" data-id="element-1249">
                        <Badge variant="primary" size="sm" data-id="element-1250">
                          Stap 1
                        </Badge>
                        <h3 className="text-lg font-bold text-slate-900" data-id="element-1251">
                          Begin hiermee
                        </h3>
                      </div>

                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-1252">
                        <div data-id="element-1253">
                          <div className="text-sm text-slate-500 mb-1" data-id="element-1254">
                            {actionPlan.beginMet.moduleTitle} ›{' '}
                            {actionPlan.beginMet.skillName}
                          </div>
                          <div className="text-xl font-bold text-slate-800 mb-2" data-id="element-1255">
                            {actionPlan.beginMet.familyDescription}
                          </div>
                          <p className="text-slate-600 max-w-xl" data-id="element-1256">
                            Dit onderwerp vond je lastig tijdens de toets. Door
                            hier mee te beginnen maak je de meeste winst!
                          </p>
                        </div>
                        <Button onClick={onPracticeWeakAreas} variant="primary" size="lg" className="bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200 whitespace-nowrap" data-id="element-1257">
                          Start Oefenen <ArrowRight className="w-4 h-4 ml-2" data-id="element-1258" />
                        </Button>
                      </div>
                    </div>
                  </Card>}

                {/* 2. Ga Verder Met */}
                {actionPlan.gaVerderMet.length > 0 && <Card className="border-l-4 border-l-emerald-500 overflow-hidden" data-id="element-1259">
                    <div className="p-6" data-id="element-1260">
                      <div className="flex items-center gap-2 mb-4" data-id="element-1261">
                        <Badge variant="success" size="sm" data-id="element-1262">
                          Stap 2
                        </Badge>
                        <h3 className="text-lg font-bold text-slate-900" data-id="element-1263">
                          Ga verder met
                        </h3>
                      </div>

                      <div className="space-y-4" data-id="element-1264">
                        {actionPlan.gaVerderMet.map((item, idx) => <div key={idx} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl" data-id="element-1265">
                            <div data-id="element-1266">
                              <div className="text-xs text-slate-500 mb-1" data-id="element-1267">
                                {item.moduleTitle}
                              </div>
                              <div className="font-bold text-slate-800" data-id="element-1268">
                                {item.familyDescription}
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-slate-400" data-id="element-1269" />
                          </div>)}
                      </div>
                    </div>
                  </Card>}

                {/* 3. Uitdaging */}
                {actionPlan.uitdaging && <Card className="border-l-4 border-l-yellow-400 overflow-hidden bg-gradient-to-r from-yellow-50 to-white" data-id="element-1270">
                    <div className="p-6" data-id="element-1271">
                      <div className="flex items-center gap-2 mb-4" data-id="element-1272">
                        <Badge variant="warning" size="sm" data-id="element-1273">
                          Uitdaging
                        </Badge>
                        <h3 className="text-lg font-bold text-slate-900" data-id="element-1274">
                          Klaar voor meer?
                        </h3>
                      </div>

                      <div className="flex items-center gap-4" data-id="element-1275">
                        <div className="bg-yellow-100 p-3 rounded-full" data-id="element-1276">
                          <Trophy className="w-6 h-6 text-yellow-600" data-id="element-1277" />
                        </div>
                        <div data-id="element-1278">
                          <div className="font-bold text-slate-800 text-lg" data-id="element-1279">
                            {actionPlan.uitdaging.suggestion}
                          </div>
                          <div className="text-slate-600" data-id="element-1280">
                            Je bent erg goed in{' '}
                            {actionPlan.uitdaging.moduleTitle}. Tijd voor het
                            volgende niveau!
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>}
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer */}
      <div className="flex-none bg-white border-t border-slate-200 p-4 z-10" data-id="element-1281">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row gap-3" data-id="element-1282">
          {onPracticeWeakAreas && <Button onClick={onPracticeWeakAreas} variant="primary" size="lg" className="flex-1 bg-indigo-600 hover:bg-indigo-700 shadow-indigo-200" data-id="element-1283">
              <BookOpen className="w-5 h-5 mr-2" data-id="element-1284" />
              Direct oefenen
            </Button>}
          <Button onClick={onExit} variant="secondary" size="lg" className="flex-1" data-id="element-1285">
            Terug naar Dashboard
          </Button>
        </div>
      </div>
    </div>;
}